import React, { useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WatchDemo from "@/components/WatchDemo";

export default function Register() {
  const { user, register, error: authError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [advisorCode, setAdvisorCode] = useState("");
  const [acceptTos, setAcceptTos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [localError, setLocalError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Whitelisted post-signup destination (e.g. ?next=compass from the Compass
  // storefront, so buyers land back on the sales page to finish checkout).
  const nextPath = searchParams.get("next") === "compass" ? "/compass" : null;

  if (user && user !== false) return <Navigate to={nextPath || "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTos) return;
    setLoading(true);
    setLocalError("");
    const result = await register(
      email,
      password,
      name,
      acceptTos,
      nextPath === "/compass" ? "" : advisorCode.trim(),
      nextPath === "/compass" ? "compass" : undefined
    );
    setLoading(false);
    if (result?.ok) {
      // Advisors (valid code) get comped access — straight into the studio.
      // Everyone else: no free tier — send to pricing to subscribe.
      navigate(nextPath || (result.isAdvisor ? "/dashboard" : "/pricing"));
      return;
    }
    const detail = result?.detail;
    if (typeof detail === "string") {
      setLocalError(detail);
    } else if (detail?.message) {
      setLocalError(detail.message);
    } else if (Array.isArray(detail)) {
      setLocalError(detail.map((d) => d.msg || "").filter(Boolean).join(" "));
    } else {
      setLocalError("Registration failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <div className="hidden lg:block relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/auth-hero.png)` }}>
        <div className="absolute inset-0 bg-emerald-950/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.3em] opacity-80">{nextPath ? "Alaine's Compass™" : "Alaine's Bench™"}</div>
          <h1 className="text-5xl font-bold mt-2 leading-tight">
            {nextPath ? "A guided path from recipe to retail." : "Bring your formulation to market — compliantly."}
          </h1>
          <p className="mt-4 text-white/80 max-w-md">
            {nextPath ? "Build your founder roadmap with practical stages, templates, and resources." : "Built for food scientists, seasoning blenders, and CPG founders."}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Create your account</h2>
            <p className="text-stone-500 text-sm mt-1">
              {nextPath ? "Create your account, then return to your Compass plan." : "Create your account, then choose a plan to unlock the studio."}
            </p>
          </div>
          <div>
            <Label htmlFor="name">Your name</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 border-stone-300 focus-visible:ring-emerald-500" data-testid="register-name-input"/>
          </div>
          <div>
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 border-stone-300 focus-visible:ring-emerald-500" data-testid="register-email-input"/>
          </div>
          <div>
            <Label htmlFor="password">Password (min 6)</Label>
            <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 border-stone-300 focus-visible:ring-emerald-500" data-testid="register-password-input"/>
          </div>
          {!nextPath && (
            <div>
              <Label htmlFor="advisor-code">Advisor code <span className="text-stone-400 font-normal">(optional)</span></Label>
              <Input id="advisor-code" value={advisorCode} onChange={(e) => setAdvisorCode(e.target.value)}
                placeholder="Invited advisors: enter your code"
                className="mt-1 border-stone-300 focus-visible:ring-emerald-500" data-testid="register-advisor-code-input"/>
              <p className="text-[11px] text-stone-400 mt-1">Founding Product Advisors get 2 weeks of full access — no card required.</p>
            </div>
          )}
          {(localError || authError) && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-2" data-testid="register-error">
              {localError || authError}
            </div>
          )}
          <label className="flex items-start gap-2 text-xs text-stone-600 leading-relaxed cursor-pointer" data-testid="accept-tos-label">
            <input type="checkbox" checked={acceptTos} onChange={(e) => setAcceptTos(e.target.checked)}
              className="mt-0.5 accent-emerald-800" data-testid="accept-tos-checkbox" />
            <span>
              I agree to the <Link to="/terms" target="_blank" className="text-emerald-800 underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-emerald-800 underline">Privacy Policy</Link>, and I understand that {nextPath ? "Alaine's Compass provides educational guidance" : "Alaine's Bench is a calculation tool"} only — <b>not a substitute for laboratory analysis or legal advice</b>. I am solely responsible for verifying compliance before commercial distribution.
            </span>
          </label>
          <Button type="submit" className="w-full bg-emerald-800 hover:bg-emerald-700 text-white" disabled={loading || !acceptTos}
            data-testid="register-submit-button">
            {loading ? "Creating account…" : "Create account"}
          </Button>
          {!nextPath && (
            <>
              <button
                type="button"
                onClick={() => setShowDemo(true)}
                className="w-full text-sm border border-emerald-800 text-emerald-800 hover:bg-emerald-50 rounded-md py-2 transition-colors"
                data-testid="register-watch-demo-button">
                ▶ Watch the demo
              </button>
              {showDemo && <WatchDemo onClose={() => setShowDemo(false)} />}
            </>
          )}
          <div className="text-sm text-stone-500 text-center">
            Have an account? <Link to={nextPath ? "/login?next=compass" : "/login"} className="text-emerald-800 font-medium hover:underline">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
